import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { DestinationCard } from "@/components/DestinationCard";
import { Button } from "@/components/ui/button";
import { Hotel, ShieldCheck, CreditCard, Headphones, ArrowRight, ChevronRight, Palmtree } from "lucide-react";
import { Link } from "@/lib/react-router-dom-shim";
import { LazyFooter } from "@/components/LazyFooter";
import { useTranslation } from "react-i18next";
import { SchemaOrg, getOrganizationSchema, getWebSiteSchema, getFAQSchema, getOnlineBusinessSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { useLocale } from "@/contexts/LocaleContext";
import { LocaleKey } from "@/i18n";
import { RESORT_DESTINATIONS } from "@/types/resort";
import { getMobileOptimizedUrl } from "@/utils/imageOptimization";
import { HomeFeaturedDestinations } from "@/components/HomeFeaturedDestinations";

const Index = () => {
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();

  const destinations = [
    {
      id: "1",
      name: "Río de Janeiro",
      location: t('home.destinations.rio.location'),
      // Foto distinta do hero para evitar carregar o mesmo asset 2x (PageSpeed P4-A).
      image: "https://images.unsplash.com/photo-1544989164-31dc3c645987?auto=format&fit=crop&w=800&q=60",
      description: t('home.destinations.rio.description'),
      rating: 4.9,
      link: "/hoteles",
    },
    {
      id: "2",
      name: "Fernando de Noronha",
      location: "Pernambuco",
      image: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=800&q=80",
      description: t('home.destinations.noronha.description'),
      rating: 5.0,
      link: "/hoteles",
    },
    {
      id: "3",
      name: "Amazonas",
      location: t('home.destinations.amazonas.location'),
      image: "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=800&q=80",
      description: t('home.destinations.amazonas.description'),
      rating: 4.8,
      link: "/hoteles",
    },
  ];

  const features = [
    {
      icon: ShieldCheck,
      title: t('home.features.secureBooking.title'),
      description: t('home.features.secureBooking.description'),
    },
    {
      icon: CreditCard,
      title: t('home.features.easyPayment.title'),
      description: t('home.features.easyPayment.description'),
    },
    {
      icon: Headphones,
      title: t('home.features.support.title'),
      description: t('home.features.support.description'),
    },
  ];

  // FAQ schema for homepage
  const faqSchema = getFAQSchema([
    {
      question: t('home.faq.whyBook.question'),
      answer: t('home.faq.whyBook.answer')
    },
    {
      question: t('home.faq.destinations.question'),
      answer: t('home.faq.destinations.answer')
    },
    {
      question: t('home.faq.payment.question'),
      answer: t('home.faq.payment.answer')
    }
  ]);

  return (
    <div className="min-h-screen bg-background">
      <HreflangTags path="/" />
<SchemaOrg schema={[getOrganizationSchema(locale as LocaleKey), getWebSiteSchema(locale as LocaleKey), getOnlineBusinessSchema(locale as LocaleKey), faqSchema]} />
      <Navbar />
      <Hero />
      
      <main id="main-content" role="main">
        {/* Destinations Section */}
        <section className="py-12 md:py-16" aria-labelledby="destinations-heading">
          <div className="container mx-auto px-4">
            <div className="mb-8 flex flex-col items-center justify-between gap-4 md:flex-row">
              <div>
                <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-primary">
                  {t('destinations.subtitle')}
                </p>
                <h2 id="destinations-heading" className="font-serif text-3xl font-bold text-foreground md:text-4xl">
                  {t('destinations.title')}
                </h2>
              </div>
              <Link 
                to="/hoteles" 
                className="group flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
              >
                {t('common.viewAll')}
                <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {destinations.map((destination) => (
                <DestinationCard key={destination.id} {...destination} />
              ))}
            </div>
          </div>
        </section>

        {/* Category Section */}
        <section className="bg-muted py-12 md:py-16 content-visibility-auto" aria-labelledby="categories-heading">
          <div className="container mx-auto px-4">
            <div className="mb-8 flex flex-col items-center justify-between gap-4 md:flex-row">
              <div>
                <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-primary">
                  {t('home.categories.subtitle')}
                </p>
                <h2 id="categories-heading" className="font-serif text-3xl font-bold text-foreground md:text-4xl">
                  {t('home.categories.title')}
                </h2>
              </div>
              <Link 
                to="/hoteles" 
                className="group flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
              >
                {t('common.viewAll')}
                <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            
            <div className="grid gap-6 sm:grid-cols-2 max-w-2xl mx-auto">
              <Link to="/hoteles" className="group">
                <div className="relative overflow-hidden rounded-2xl bg-card p-8 text-center shadow-elegant transition-all duration-300 hover:shadow-elegant-hover">
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 transition-all duration-300 group-hover:bg-primary group-hover:scale-110">
                    <Hotel className="h-10 w-10 text-primary transition-colors group-hover:text-primary-foreground" />
                  </div>
                  <h3 className="mb-3 font-serif text-2xl font-bold text-foreground">{t('nav.hotels')}</h3>
                  <p className="mb-6 text-muted-foreground">
                    {t('home.categories.hotels.description')}
                  </p>
                  <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                    {t('common.explore')}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
              <Link to="/resorts" className="group">
                <div className="relative overflow-hidden rounded-2xl bg-card p-8 text-center shadow-elegant transition-all duration-300 hover:shadow-elegant-hover">
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 transition-all duration-300 group-hover:bg-primary group-hover:scale-110">
                    <Palmtree className="h-10 w-10 text-primary transition-colors group-hover:text-primary-foreground" />
                  </div>
                  <h3 className="mb-3 font-serif text-2xl font-bold text-foreground">{t('nav.resorts')}</h3>
                  <p className="mb-6 text-muted-foreground">
                    {t('home.categories.resorts.description')}
                  </p>
                  <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                    {t('common.explore')}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Resort Destinations Section */}
        <section className="py-12 md:py-16 content-visibility-auto" aria-labelledby="resort-destinations-heading">
          <div className="container mx-auto px-4">
            <div className="mb-8 flex flex-col items-center justify-between gap-4 md:flex-row">
              <div>
                <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-primary">
                  {t('home.resortDestinations.subtitle')}
                </p>
                <h2 id="resort-destinations-heading" className="font-serif text-3xl font-bold text-foreground md:text-4xl">
                  {t('home.resortDestinations.title')}
                </h2>
              </div>
              <Link 
                to="/resorts" 
                className="group flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
              >
                {t('resorts.allResorts')}
                <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {RESORT_DESTINATIONS.map((dest, index) => (
                <Link 
                  key={dest.id}
                  to={`/resorts/${dest.id}`}
                  className="group relative overflow-hidden rounded-xl aspect-[4/5] shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <img 
                    src={getMobileOptimizedUrl(dest.image, 400, 60)}
                    srcSet={`${getMobileOptimizedUrl(dest.image, 280, 60)} 280w, ${getMobileOptimizedUrl(dest.image, 400, 60)} 400w, ${getMobileOptimizedUrl(dest.image, 560, 50)} 560w`}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                    alt={t(dest.nameKey) || dest.name}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                    width={280}
                    height={350}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-lg font-bold text-primary-foreground leading-tight">
                      {t(dest.nameKey) || dest.name}
                    </h3>
                    <p className="text-xs text-primary-foreground/70 mt-1 flex items-center gap-1 group-hover:text-primary-foreground transition-colors">
                      {t('common.explore')}
                      <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Destination Guides (SEO-first) */}
        <HomeFeaturedDestinations limit={6} />

        {/* Features Section */}
        <section className="py-12 md:py-16 content-visibility-auto" aria-labelledby="features-heading">
          <div className="container mx-auto px-4">
            <div className="mb-10 text-center">
              <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-primary">
                {t('home.features.subtitle')}
              </p>
              <h2 id="features-heading" className="font-serif text-3xl font-bold text-foreground md:text-4xl">
                {t('home.features.title')}
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="group rounded-2xl border border-border bg-card p-8 text-center transition-all duration-300 hover:border-primary/20 hover:shadow-elegant"
                >
                  <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 transition-all duration-300 group-hover:bg-primary">
                    <feature.icon className="h-8 w-8 text-primary transition-colors group-hover:text-primary-foreground" />
                  </div>
                  <h3 className="mb-3 font-serif text-xl font-bold text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Content Section - Rich text for search engines */}
        <section className="bg-muted py-12 md:py-16 content-visibility-auto" aria-labelledby="about-heading">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl">
              <div className="mb-8 text-center">
                <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-primary">
                  {t('home.seoContent.subtitle')}
                </p>
                <h2 id="about-heading" className="font-serif text-3xl font-bold text-foreground md:text-4xl">
                  {t('home.seoContent.title')}
                </h2>
              </div>
              
              <div className="prose prose-lg max-w-none text-muted-foreground">
                <p className="mb-6 leading-relaxed">
                  {t('home.seoContent.paragraph1')}
                </p>
                <p className="mb-8 leading-relaxed">
                  {t('home.seoContent.paragraph2')}
                </p>
                
                <div className="grid gap-8 md:grid-cols-2">
                  <div className="rounded-xl border border-border bg-card p-6">
                    <h3 className="mb-3 font-serif text-xl font-bold text-foreground">
                      {t('home.seoContent.destinationsTitle')}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {t('home.seoContent.destinationsText')}
                    </p>
                  </div>
                  
                  <div className="rounded-xl border border-border bg-card p-6">
                    <h3 className="mb-3 font-serif text-xl font-bold text-foreground">
                      {t('home.seoContent.resortsTitle')}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {t('home.seoContent.resortsText')}
                    </p>
                  </div>
                </div>
                
                <div className="mt-8 rounded-xl border border-primary/20 bg-primary/5 p-6">
                  <h3 className="mb-3 font-serif text-xl font-bold text-foreground">
                    {t('home.seoContent.whyUsTitle')}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {t('home.seoContent.whyUsText')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 content-visibility-auto">
          <div className="container mx-auto px-4">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-hero p-12 md:p-20">
              {/* Decorative Elements */}
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary-foreground/10" />
              <div className="absolute -bottom-10 -left-10 h-48 w-48 rounded-full bg-primary-foreground/10" />
              
              <div className="relative z-10 mx-auto max-w-2xl text-center">
                <h2 className="mb-6 font-serif text-3xl font-bold text-primary-foreground md:text-4xl lg:text-5xl">
                  {t('home.cta.title')}
                </h2>
                <p className="mb-10 text-lg text-primary-foreground/80 md:text-xl">
                  {t('home.cta.description')}
                </p>
                <Button 
                  size="lg" 
                  className="h-14 rounded-md bg-card px-10 text-base font-semibold text-foreground shadow-lg transition-all hover:bg-card/90 hover:shadow-xl" 
                  asChild
                >
                  <Link to="/hoteles" aria-label={t('home.cta.button')}>
                    {t('home.cta.button')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <LazyFooter />
    </div>
  );
};

export default Index;
